import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiCheck,
  FiMapPin, FiMail, FiPhone, FiGlobe, FiUsers, FiPackage,
  FiToggleLeft, FiToggleRight, FiCalendar, FiDollarSign,
  FiSend, FiCreditCard, FiCopy, FiRefreshCw, FiLink
} from 'react-icons/fi';
import { dealerService } from '../../services/dealerService';
import { dealerOnboardingService } from '../../services/dealerOnboardingService';
import { stripeConnectService } from '../../services/stripeConnectService';

const MasterDealerManager = ({ onNavigate }) => {
  const [dealers, setDealers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [activeTab, setActiveTab] = useState('dealers');
  const [inviting, setInviting] = useState(null);
  const [inviteUrl, setInviteUrl] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDealerId, setInviteDealerId] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const [dealerForm, setDealerForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    subscription_status: 'trial',
    subscription_plan: 'basic',
    max_sheds: 50,
    is_active: true,
    can_publish: true,
    lot_locations: []
  });

  const hasMultiLocation = dealerForm.subscription_plan === 'pro' || dealerForm.subscription_plan === 'enterprise';

  const addLotLocation = () => {
    setDealerForm(prev => ({
      ...prev,
      lot_locations: [
        ...(prev.lot_locations || []),
        { id: crypto.randomUUID(), name: '', address: '', city: '', state: '', zip: '' }
      ]
    }));
  };

  const updateLotLocation = (id, field, value) => {
    setDealerForm(prev => ({
      ...prev,
      lot_locations: (prev.lot_locations || []).map(loc =>
        loc.id === id ? { ...loc, [field]: value } : loc
      )
    }));
  };

  const removeLotLocation = (id) => {
    setDealerForm(prev => ({
      ...prev,
      lot_locations: (prev.lot_locations || []).filter(loc => loc.id !== id)
    }));
  };

  const [cityForm, setCityForm] = useState({
    name: '',
    state: '',
    state_abbr: '',
    radius_miles: 30,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [dealersRes, citiesRes] = await Promise.all([
      dealerService.getAllDealers(),
      dealerService.getAllCities()
    ]);
    setDealers(dealersRes.data || []);
    setCities(citiesRes.data || []);
    setLoading(false);
    loadInvitations();
  };

  const loadInvitations = async () => {
    setLoadingInvites(true);
    const res = await dealerOnboardingService.getAllInvitations();
    setInvitations(res.data || []);
    setLoadingInvites(false);
  };

  const filteredDealers = dealers.filter(dealer =>
    dealer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dealer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dealer.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddDealer = () => {
    setSelectedDealer(null);
    setDealerForm({
      company_name: '',
      email: '',
      phone: '',
      website: '',
      address_line1: '',
      city: '',
      state: '',
      zip_code: '',
      subscription_status: 'trial',
      subscription_plan: 'basic',
      max_sheds: 50,
      is_active: true,
      can_publish: true,
      lot_locations: []
    });
    setShowModal(true);
  };

  const handleEditDealer = (dealer) => {
    setSelectedDealer(dealer);
    setDealerForm({
      company_name: dealer.company_name || '',
      email: dealer.email || '',
      phone: dealer.phone || '',
      website: dealer.website || '',
      address_line1: dealer.address_line1 || '',
      city: dealer.city || '',
      state: dealer.state || '',
      zip_code: dealer.zip_code || '',
      subscription_status: dealer.subscription_status || 'trial',
      subscription_plan: dealer.subscription_plan || 'basic',
      max_sheds: dealer.max_sheds || 50,
      is_active: dealer.is_active ?? true,
      can_publish: dealer.can_publish ?? true,
      lot_locations: dealer.lot_locations || []
    });
    setShowModal(true);
  };

  const handleSubmitDealer = async (e) => {
    e.preventDefault();
    
    try {
      let result;
      if (selectedDealer) {
        result = await dealerService.updateDealer(selectedDealer.id, dealerForm);
      } else {
        result = await dealerService.createDealer(dealerForm);
      }
      
      if (result.error) {
        alert('Error saving dealer: ' + (result.error.message || JSON.stringify(result.error)));
        return;
      }
      
      setShowModal(false);
      loadData();
    } catch (err) {
      alert('Error saving dealer: ' + err.message);
    }
  };

  const handleDeleteDealer = async (dealer) => {
    if (!confirm(`Delete ${dealer.company_name}? This will remove all their data.`)) return;
    
    try {
      await dealerService.deleteDealer(dealer.id);
      loadData();
    } catch (err) {
      alert('Error deleting dealer: ' + err.message);
    }
  };

  const handleToggleActive = async (dealer) => {
    try {
      await dealerService.toggleDealerStatus(dealer.id, !dealer.is_active);
      loadData();
    } catch (err) {
      alert('Error updating dealer: ' + err.message);
    }
  };

  const handleAddCity = () => {
    setCityForm({
      name: '',
      state: '',
      state_abbr: '',
      radius_miles: 30,
      is_active: true
    });
    setShowCityModal(true);
  };

  const handleSubmitCity = async (e) => {
    e.preventDefault();
    
    try {
      const result = await dealerService.createCity(cityForm);
      
      if (result.error) {
        alert('Error saving city: ' + (result.error.message || JSON.stringify(result.error)));
        return;
      }
      
      setShowCityModal(false);
      loadData();
    } catch (err) {
      alert('Error saving city: ' + err.message);
    }
  };

  const handleDeleteCity = async (city) => {
    if (!confirm(`Delete ${city.name}, ${city.state_abbr}?`)) return;
    
    try {
      await dealerService.deleteCity(city.id);
      loadData();
    } catch (err) {
      alert('Error deleting city: ' + err.message);
    }
  };

  // ==========================================
  // INVITE & STRIPE CONNECT HANDLERS
  // ==========================================

  const handleOpenInvite = (dealer) => {
    setInviteDealerId(dealer.id);
    setInviteEmail(dealer.email || '');
    setInviteUrl('');
    setShowInviteModal(true);
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      alert('Please enter an email address');
      return;
    }
    setInviting(inviteDealerId);
    try {
      const result = await dealerOnboardingService.inviteDealer(inviteDealerId, inviteEmail);
      if (result.error) {
        alert('Error sending invite: ' + (result.error.message || JSON.stringify(result.error)));
      } else {
        setInviteUrl(result.data?.invite_url || '');
        alert('Invite sent to ' + inviteEmail);
        loadData();
      }
    } catch (err) {
      alert('Error sending invite: ' + err.message);
    } finally {
      setInviting(null);
    }
  };

  const handleCopyInviteUrl = () => {
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl);
      alert('Invite URL copied to clipboard');
    }
  };

  const handleConnectStripe = (dealer) => {
    const url = stripeConnectService.getConnectOAuthUrl(dealer.id);
    window.open(url, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPlanColor = (plan) => {
    const colors = {
      basic: 'bg-gray-100 text-gray-800',
      pro: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800'
    };
    return colors[plan] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dealer Management</h1>
          <p className="text-gray-600">Manage licensed dealers and service areas</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('dealers')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'dealers' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FiUsers className="inline mr-2" />
          Dealers ({dealers.length})
        </button>
        <button
          onClick={() => setActiveTab('cities')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'cities' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FiMapPin className="inline mr-2" />
          Cities ({cities.length})
        </button>
        <button
          onClick={() => { setActiveTab('invites'); loadInvitations(); }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'invites' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FiSend className="inline mr-2" />
          Invites Sent ({invitations.length})
        </button>
      </div>

      {/* Dealers Tab */}
      {activeTab === 'dealers' && (
        <>
          {/* Search & Add */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search dealers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={handleAddDealer}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FiPlus /> Add Dealer
            </button>
          </div>

          {/* Dealers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDealers.map(dealer => (
              <motion.div
                key={dealer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{dealer.company_name}</h3>
                    <p className="text-sm text-gray-500">{dealer.city}, {dealer.state}</p>
                  </div>
                  <button
                    onClick={() => handleToggleActive(dealer)}
                    className={`p-1 rounded ${dealer.is_active ? 'text-green-600' : 'text-gray-400'}`}
                  >
                    {dealer.is_active ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
                  </button>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FiMail size={14} />
                    <span className="truncate">{dealer.email}</span>
                  </div>
                  {dealer.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <FiPhone size={14} />
                      <span>{dealer.phone}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(dealer.subscription_status)}`}>
                    {dealer.subscription_status}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(dealer.subscription_plan)}`}>
                    {dealer.subscription_plan}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span><FiPackage className="inline mr-1" /> Max {dealer.max_sheds} sheds</span>
                </div>

                {/* Stripe Status */}
                <div className="flex items-center gap-2 text-xs mb-3">
                  {dealer.stripe_connected ? (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full">
                      <FiCheck size={12} /> Stripe Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full">
                      <FiCreditCard size={12} /> No Stripe
                    </span>
                  )}
                  {dealer.onboarding_status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      dealer.onboarding_status === 'account_created' ? 'bg-blue-50 text-blue-700'
                      : dealer.onboarding_status === 'invited' ? 'bg-amber-50 text-amber-700'
                      : 'bg-gray-100 text-gray-500'
                    }`}>
                      {dealer.onboarding_status === 'account_created' ? 'Onboarded' : dealer.onboarding_status}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleEditDealer(dealer)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <FiEdit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleOpenInvite(dealer)}
                    disabled={inviting === dealer.id}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                  >
                    <FiSend size={14} /> {inviting === dealer.id ? 'Sending...' : 'Invite'}
                  </button>
                  <button
                    onClick={() => handleDeleteDealer(dealer)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <FiTrash2 size={14} /> Delete
                  </button>
                  {onNavigate && (
                    <button
                      onClick={() => onNavigate('sheds')}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg"
                    >
                      <FiPackage size={14} /> Sheds
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {filteredDealers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FiUsers size={48} className="mx-auto mb-4 opacity-50" />
              <p>No dealers found</p>
            </div>
          )}
        </>
      )}

      {/* Cities Tab */}
      {activeTab === 'cities' && (
        <>
          <div className="flex justify-end mb-6">
            <button
              onClick={handleAddCity}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FiPlus /> Add City
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">City</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">State</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Radius</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cities.map(city => (
                  <tr key={city.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{city.name}</td>
                    <td className="px-4 py-3 text-gray-600">{city.state_abbr || city.state}</td>
                    <td className="px-4 py-3 text-gray-600">{city.radius_miles} mi</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        city.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {city.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDeleteCity(city)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {cities.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FiMapPin size={48} className="mx-auto mb-4 opacity-50" />
              <p>No cities configured</p>
            </div>
          )}
        </>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">All dealer invitation records with onboarding links</p>
            <button
              onClick={loadInvitations}
              disabled={loadingInvites}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiRefreshCw size={14} className={loadingInvites ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {loadingInvites ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FiSend size={48} className="mx-auto mb-4 opacity-50" />
              <p>No invitations sent yet</p>
              <p className="text-sm mt-1">Go to the Dealers tab and click "Invite" on a dealer to send one.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Dealer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Sent</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Expires</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Invite Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invitations.map(inv => {
                    const isExpired = inv.status === 'pending' && new Date(inv.expires_at) < new Date();
                    const statusLabel = inv.status === 'accepted' ? 'Accepted' : isExpired ? 'Expired' : inv.status === 'cancelled' ? 'Cancelled' : 'Pending';
                    const statusColor = inv.status === 'accepted' ? 'bg-green-100 text-green-800' : isExpired ? 'bg-red-100 text-red-800' : inv.status === 'cancelled' ? 'bg-gray-100 text-gray-800' : 'bg-amber-100 text-amber-800';
                    const invUrl = `${window.location.origin}/dealer/onboard?token=${inv.invite_token}`;

                    return (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 text-sm">
                          {inv.dealers?.company_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-sm">{inv.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {new Date(inv.invited_at).toLocaleDateString()}{' '}
                          {new Date(inv.invited_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {new Date(inv.expires_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="text"
                              readOnly
                              value={invUrl}
                              className="w-48 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[11px] text-gray-500 font-mono truncate"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(invUrl);
                                alert('Invite link copied');
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Copy invite link"
                            >
                              <FiCopy size={14} />
                            </button>
                            {(inv.status === 'pending' && !isExpired) && (
                              <button
                                onClick={async () => {
                                  const res = await dealerOnboardingService.resendInvite(inv.id);
                                  if (res.error) {
                                    alert('Error resending: ' + (res.error.message || JSON.stringify(res.error)));
                                  } else {
                                    alert('Invite resent to ' + inv.email);
                                    loadInvitations();
                                  }
                                }}
                                className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                title="Resend invite"
                              >
                                <FiRefreshCw size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Dealer Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-semibold">
                  {selectedDealer ? 'Edit Dealer' : 'Add New Dealer'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmitDealer} className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={dealerForm.company_name}
                      onChange={e => setDealerForm({...dealerForm, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={dealerForm.email}
                      onChange={e => setDealerForm({...dealerForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={dealerForm.phone}
                      onChange={e => setDealerForm({...dealerForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      value={dealerForm.website}
                      onChange={e => setDealerForm({...dealerForm, website: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={dealerForm.address_line1}
                      onChange={e => setDealerForm({...dealerForm, address_line1: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={dealerForm.city}
                      onChange={e => setDealerForm({...dealerForm, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={dealerForm.state}
                      onChange={e => setDealerForm({...dealerForm, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      value={dealerForm.zip_code}
                      onChange={e => setDealerForm({...dealerForm, zip_code: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <hr className="my-4" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                    <select
                      value={dealerForm.subscription_status}
                      onChange={e => setDealerForm({...dealerForm, subscription_status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                    <select
                      value={dealerForm.subscription_plan}
                      onChange={e => setDealerForm({...dealerForm, subscription_plan: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Sheds</label>
                    <input
                      type="number"
                      min="1"
                      value={dealerForm.max_sheds}
                      onChange={e => setDealerForm({...dealerForm, max_sheds: parseInt(e.target.value) || 50})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dealerForm.is_active}
                      onChange={e => setDealerForm({...dealerForm, is_active: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dealerForm.can_publish}
                      onChange={e => setDealerForm({...dealerForm, can_publish: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Can Publish Sheds</span>
                  </label>
                </div>

                {/* Lot Locations — Pro & Enterprise only */}
                {hasMultiLocation && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                          <FiMapPin className="w-4 h-4 text-emerald-600" />
                          Lot Locations
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">Add all shed lot/yard locations for this dealer</p>
                      </div>
                      <button
                        type="button"
                        onClick={addLotLocation}
                        className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <FiPlus className="w-3.5 h-3.5" />
                        Add Location
                      </button>
                    </div>

                    {(!dealerForm.lot_locations || dealerForm.lot_locations.length === 0) && (
                      <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500 border border-dashed border-gray-300">
                        No lot locations added yet. Click "Add Location" to add one.
                      </div>
                    )}

                    <div className="space-y-3">
                      {(dealerForm.lot_locations || []).map((loc, idx) => (
                        <div key={loc.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200 relative">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500">Location #{idx + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeLotLocation(loc.id)}
                              className="text-red-400 hover:text-red-600 p-1 hover:bg-red-50 rounded transition-colors"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="sm:col-span-2">
                              <input
                                type="text"
                                placeholder="Location name (e.g., Main Lot, North Yard)"
                                value={loc.name}
                                onChange={e => updateLotLocation(loc.id, 'name', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <input
                                type="text"
                                placeholder="Street address"
                                value={loc.address}
                                onChange={e => updateLotLocation(loc.id, 'address', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <input
                                type="text"
                                placeholder="City"
                                value={loc.city}
                                onChange={e => updateLotLocation(loc.id, 'city', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                placeholder="State"
                                value={loc.state}
                                onChange={e => updateLotLocation(loc.id, 'state', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              />
                              <input
                                type="text"
                                placeholder="ZIP"
                                value={loc.zip}
                                onChange={e => updateLotLocation(loc.id, 'zip', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {selectedDealer ? 'Update Dealer' : 'Create Dealer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* City Modal */}
      <AnimatePresence>
        {showCityModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCityModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-semibold">Add City</h2>
                <button onClick={() => setShowCityModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmitCity} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City Name *</label>
                  <input
                    type="text"
                    required
                    value={cityForm.name}
                    onChange={e => setCityForm({...cityForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Dallas"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <input
                      type="text"
                      required
                      value={cityForm.state}
                      onChange={e => setCityForm({...cityForm, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Texas"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State Abbr *</label>
                    <input
                      type="text"
                      required
                      maxLength={2}
                      value={cityForm.state_abbr}
                      onChange={e => setCityForm({...cityForm, state_abbr: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="TX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Radius (miles)</label>
                  <input
                    type="number"
                    min="1"
                    value={cityForm.radius_miles}
                    onChange={e => setCityForm({...cityForm, radius_miles: parseInt(e.target.value) || 30})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cityForm.is_active}
                    onChange={e => setCityForm({...cityForm, is_active: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCityModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Add City
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Dealer Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-semibold">Invite Dealer</h2>
                <button onClick={() => setShowInviteModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FiX size={24} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Send an invitation email so this dealer can create their account, connect Stripe and manage their inventory.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Email *</label>
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="dealer@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {inviteUrl && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Invite URL (copy if email didn't send)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={inviteUrl}
                        className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-600"
                      />
                      <button
                        onClick={handleCopyInviteUrl}
                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                      >
                        <FiCopy size={14} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleSendInvite}
                    disabled={inviting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    <FiSend size={14} />
                    {inviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MasterDealerManager;
